import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Shield, Radar, Cpu, Sparkles, Cloud, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import Logo from './Logo';

interface TopBarProps {
    className?: string;
    showLogo?: boolean;
    showNav?: boolean;
}

interface ProductUseCase {
    icon: typeof Cpu;
    labelKey: string;
    to: string;
    color: string;
}

interface ProductItem {
    icon: typeof Cpu;
    labelKey: string;
    descKey: string;
    to: string;
    color: string;
    bg: string;
    borderColor: string;
    useCases?: ProductUseCase[];
}

const productItemDefs: ProductItem[] = [
    {
        icon: Cpu,
        labelKey: 'topbar.reverse_model_name',
        descKey: 'topbar.reverse_model_desc',
        to: '/products/reverse-model',
        color: 'text-emerald-400',
        bg: 'bg-emerald-500/10',
        borderColor: 'border-emerald-500/20',
        useCases: [
            {
                icon: Shield,
                labelKey: 'topbar.malware_analysis',
                to: '/products/malware-analysis',
                color: 'text-blue-400',
            },
            {
                icon: Radar,
                labelKey: 'topbar.vuln_mining',
                to: '/products/vulnerability-mining',
                color: 'text-indigo-400',
            },
        ],
    },
];

const topLevelProductLinks = [
    {
        icon: MessageSquare,
        labelKey: 'topbar.llm_platform',
        to: '/products/llm-platform',
    },
    {
        icon: Cloud,
        labelKey: 'topbar.gpu_cloud',
        to: '/products/gpu-cloud',
    },
    {
        icon: Sparkles,
        labelKey: 'topbar.finetuning',
        to: '/products/fine-tuning',
    },
];

export default function TopBar({ className = '', showLogo = true, showNav = true }: TopBarProps) {
    const { t } = useTranslation('common');
    const location = useLocation();

    const [productMenuOpen, setProductMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        function handleClick(e: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setProductMenuOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const openMenu = () => {
        clearTimeout(timeoutRef.current);
        setProductMenuOpen(true);
    };
    const closeMenu = () => {
        timeoutRef.current = setTimeout(() => setProductMenuOpen(false), 150);
    };

    const productMenuPaths = [
        ...productItemDefs.map(item => item.to),
        ...productItemDefs.flatMap(item => item.useCases?.map(uc => uc.to) ?? []),
    ];
    const isProductActive = productMenuPaths.includes(location.pathname);

    return (
        <header className={`h-12 toolbar sticky top-0 z-40 w-full ${className}`}>
            <div className="h-full px-4 sm:px-6 flex items-center justify-between">
                {/* Logo Section - Left */}
                <div className="flex items-center">
                    {showLogo && (
                        <Link to="/" className="group flex items-center gap-2.5 transition-all">
                            <div className="relative flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors">
                                <Logo size="w-5 h-5" />
                            </div>
                            <span className="text-base font-semibold text-foreground tracking-tight group-hover:text-primary transition-colors">
                                {import.meta.env.VITE_PRODUCT_NAME || 'Etherworks'}
                            </span>
                        </Link>
                    )}
                </div>

                {/* Right Side - Navigation + User Actions */}
                <div className="flex items-center gap-6">
                    {/* Navigation Links */}
                    {showNav && (
                        <nav className="hidden md:flex items-center gap-1">
                            {/* Product Introduction with dropdown */}
                            <div
                                ref={menuRef}
                                className="relative"
                                onMouseEnter={openMenu}
                                onMouseLeave={closeMenu}
                            >
                                <button
                                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors inline-flex items-center gap-1 ${isProductActive
                                        ? 'text-primary bg-primary/10'
                                        : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                                        }`}
                                >
                                    {t('topbar.products')}
                                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${productMenuOpen ? 'rotate-180' : ''}`} />
                                </button>

                                {productMenuOpen && (
                                    <div className="absolute left-0 top-full pt-1.5 z-50">
                                        <div className="w-72 macos-menu p-1 animate-in fade-in zoom-in-95 duration-100">
                                            {productItemDefs.map((item, idx) => {
                                                const Icon = item.icon;
                                                return (
                                                    <div key={item.to} className={idx > 0 ? 'mt-1 pt-1 border-t border-border/40' : ''}>
                                                        <Link
                                                            to={item.to}
                                                            onClick={() => setProductMenuOpen(false)}
                                                            className={`flex items-center gap-3 px-2.5 py-2 rounded-md transition-colors hover:bg-primary/10 ${location.pathname === item.to ? 'bg-primary/10' : ''}`}
                                                        >
                                                            <div className={`h-7 w-7 rounded-md ${item.bg} flex items-center justify-center flex-shrink-0`}>
                                                                <Icon className={`w-3.5 h-3.5 ${item.color}`} />
                                                            </div>
                                                            <div>
                                                                <p className="text-[13px] font-medium text-foreground">{t(item.labelKey)}</p>
                                                                <p className="text-[11px] text-muted-foreground">{t(item.descKey)}</p>
                                                            </div>
                                                        </Link>
                                                        {item.useCases && item.useCases.length > 0 && (
                                                            <div className="mt-0.5 ml-10 pl-2 border-l border-border/40 space-y-0.5">
                                                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 px-2 pt-1">
                                                                    {t('topbar.use_cases')}
                                                                </p>
                                                                {item.useCases.map(uc => {
                                                                    const UcIcon = uc.icon;
                                                                    return (
                                                                        <Link
                                                                            key={uc.to}
                                                                            to={uc.to}
                                                                            onClick={() => setProductMenuOpen(false)}
                                                                            className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-[12px] text-muted-foreground hover:text-foreground hover:bg-primary/10 transition-colors ${location.pathname === uc.to ? 'bg-primary/10 text-foreground' : ''}`}
                                                                        >
                                                                            <UcIcon className={`w-3 h-3 ${uc.color}`} />
                                                                            <span>{t(uc.labelKey)}</span>
                                                                        </Link>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {topLevelProductLinks.map(link => {
                                const active = location.pathname === link.to;
                                return (
                                    <Link
                                        key={link.to}
                                        to={link.to}
                                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${active ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'}`}
                                    >
                                        {t(link.labelKey)}
                                    </Link>
                                );
                            })}

                            <Link
                                to="/plans"
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${location.pathname.startsWith('/plans') ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'}`}
                            >
                                {t('topbar.pricing')}
                            </Link>
                        </nav>
                    )}

                    {/* Language Switcher + User Actions */}
                    <LanguageSwitcher />
                </div>
            </div>
        </header>
    );
}
